import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import Button from '@/components/UI/Button';

interface LandingPageProps {
  onAccept: () => void;
  formName?: string;
}

const LandingPage = ({ onAccept, formName }: LandingPageProps) => {
  const [accepted, setAccepted] = useState(false);

  const handleAccept = () => {
    if (accepted) {
      onAccept();
    }
  };

  return (
    <div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
      <div className='sm:mx-auto sm:w-full sm:max-w-3xl'>
        <div className='bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10'>
          <div className='text-center mb-8'>
            <h2 className='text-3xl font-extrabold text-gray-900 mb-4'>Welcome to {formName || 'Birmingham Critical Repairs'}</h2>
            <p className='text-lg text-gray-600'>Please read the terms and conditions below before proceeding with your application.</p>
            <div className='mt-4'>
              <a
                href='https://stp-public-marketing-videos.s3.us-east-2.amazonaws.com/CRITICAL+REPAIR+APPLICATION+CHECKLIST.pdf'
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primaryColor-normal hover:bg-primaryColor-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primaryColor-normal transition-colors duration-200'
              >
                <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                  />
                </svg>
                Critical Repair Checklist
              </a>
            </div>
          </div>

          <div className='bg-gray-50 p-6 rounded-lg mb-8 max-h-96 overflow-y-auto'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>Terms and Conditions</h3>

            <div className='text-sm text-gray-700 space-y-4'>
              <p>
                <strong>1. Application Process</strong>
                <br />
                By submitting this application, you acknowledge that you are providing accurate and complete information. Any false
                statements may result in the rejection of your application or legal action.
              </p>

              <p>
                <strong>2. Eligibility Requirements</strong>
                <br />
                You must meet all eligibility requirements as outlined in the program guidelines. The program reserves the right to verify
                all information provided and request additional documentation.
              </p>

              <p>
                <strong>3. Privacy and Data Protection</strong>
                <br />
                Your personal information will be collected, used, and protected in accordance with applicable privacy laws. We will not
                share your information with third parties without your consent, except as required by law.
              </p>

              <p>
                <strong>4. Application Review</strong>
                <br />
                All applications will be reviewed based on program criteria. Submission of an application does not guarantee approval. The
                review process may take several weeks.
              </p>

              <p>
                <strong>5. Communication</strong>
                <br />
                You agree to receive communications regarding your application via email, phone, or mail. Please ensure your contact
                information is current and accurate.
              </p>

              <p>
                <strong>6. Program Changes</strong>
                <br />
                Program terms, conditions, and funding availability are subject to change. Applications will be processed according to the
                terms in effect at the time of submission.
              </p>

              <p>
                <strong>7. Limitation of Liability</strong>
                <br />
                The program and its administrators are not liable for any damages, losses, or expenses arising from the application process
                or program participation.
              </p>
            </div>
          </div>

          <div className='flex items-start mb-6'>
            <div className='flex items-center h-5'>
              <input
                id='terms-acceptance'
                name='terms-acceptance'
                type='checkbox'
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className='focus:ring-primaryColor-normal h-4 w-4 text-primaryColor-normal border-gray-300 rounded'
              />
            </div>
            <div className='ml-3 text-sm'>
              <label htmlFor='terms-acceptance' className='font-medium text-gray-700'>
                I have read and agree to the terms and conditions
              </label>
              <p className='text-gray-500'>You must accept the terms and conditions to proceed with your application.</p>
            </div>
          </div>

          <div className='flex justify-center'>
            <Button label='Continue to Application' btnStyle='primary' type='button' disabled={!accepted} onClick={handleAccept} />
          </div>

          {accepted && (
            <div className='mt-4 flex items-center justify-center text-green-600'>
              <CheckCircleIcon className='h-5 w-5 mr-2' />
              <span className='text-sm font-medium'>Terms accepted - Ready to proceed</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
